import React, { FC, Suspense, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { PublicPageLayout } from '@adsk/alloy-project-page-layout';
import envConfig from '../../envs/envConfig';
import {
  ProjectsRoute,
  PublicRoute,
  RemoteTool,
  ToolRoute,
  LAYOUTS,
} from '../../envs/types';
import useRemotes from '../../hooks/useRemotes';
import Projects from '../../routes/Projects/Projects';
import ProjectsLoader from '../../routes/Projects/ProjectsLoader';
import FullPageProgressRing from '../FullPageProgressRing';
import PageNotFoundExo from '../PageNotFoundExo/PageNotFoundExo';
import Redirect from '../Redirect/Redirect';
import RemoteComponent from '../RemoteComponent/RemoteComponent';
import Tool from '../Tool/Tool';

const RootProjectPageLayout = React.lazy(
  () => import('../RootProjectPageLayout')
);

const Router: FC = () => {
  const remotes = useRemotes();
  const { remoteRoutes } = envConfig;

  const redirects = [
    {
      from: '/build/projects/project-templates',
      to: '/projects/templates',
    },
    {
      from: '/projects/project-templates',
      to: '/projects/templates',
    },
    {
      from: '/build/projects',
      to: '/projects',
    },
    {
      from: '/projects/projects',
      to: '/projects',
    },
  ];

  const projectPageLayoutRoutes = useMemo(
    () =>
      remoteRoutes?.filter(
        (route) => route.layout === LAYOUTS.PROJECT_PAGE_LAYOUT
      ) as ToolRoute[],
    [remoteRoutes]
  );

  const projectDirectoryLayoutRoutes = useMemo(
    () =>
      remoteRoutes?.filter(
        (route) => route.layout === LAYOUTS.PROJECT_DIRECTORY_LAYOUT
      ) as ProjectsRoute[],
    [remoteRoutes]
  );

  const publicPageLayoutRoutes = useMemo(
    () =>
      remoteRoutes?.filter(
        (route) => route.layout === LAYOUTS.PUBLIC_PAGE_LAYOUT
      ) as PublicRoute[],
    [remoteRoutes]
  );

  return (
    <Suspense fallback={<FullPageProgressRing />}>
      <Routes>
        <Route path="/" element={<RootProjectPageLayout />}>
          {projectPageLayoutRoutes.map(({ path, scope, titleKey, toolId }) => {
            const remote = remotes?.find((r) => r.scope === scope) as
              | RemoteTool
              | undefined;

            if (!remote) {
              return null;
            }

            return (
              <Route
                key={path}
                path={path}
                element={
                  <Tool titleKey={titleKey}>
                    <RemoteComponent
                      url={remote.url}
                      scope={scope}
                      module={remote.modules.toolModule}
                      // in docs case, we need toolId to trigger reset for LazyComponent
                      toolId={toolId}
                    />
                  </Tool>
                }
              />
            );
          })}
        </Route>
        <Route element={<Projects />}>
          {projectDirectoryLayoutRoutes.map(({ path, scope, moduleKey }) => {
            const remote = remotes?.find((r) => r.scope === scope);

            if (!remote) {
              return null;
            }

            return (
              <Route
                key={path}
                path={path}
                element={
                  <RemoteComponent
                    url={remote.url}
                    scope={scope}
                    module={
                      remote.modules[moduleKey as keyof typeof remote.modules]
                    }
                    renderLoader={() => <ProjectsLoader />}
                  />
                }
              />
            );
          })}
        </Route>
        <Route element={<PublicPageLayout />}>
          {publicPageLayoutRoutes.map(({ path, scope, moduleKey }) => {
            const remote = remotes?.find((r) => r.scope === scope) as
              | RemoteTool
              | undefined;

            if (!remote) {
              return null;
            }

            return (
              <Route
                key={path}
                path={path}
                element={
                  <RemoteComponent
                    url={remote.url}
                    scope={scope}
                    module={remote.modules[moduleKey]}
                    renderLoader={() => <ProjectsLoader />}
                  />
                }
              />
            );
          })}
        </Route>
        {redirects.map(({ from, to }) => (
          <Route
            key={from}
            path={from}
            element={<Redirect from={from} to={to} />}
          />
        ))}
        <Route path="*" element={<PageNotFoundExo />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
